import { Component, computed, DestroyRef, effect, inject, input, linkedSignal, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { BrnSelectImports } from '@spartan-ng/brain/select';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmFieldImports } from '@spartan-ng/helm/field';
import { HlmSelectImports } from '@spartan-ng/helm/select';

import { RégionStore } from '../../../administration/gestion-sites/store/région.store';
import { SiteStore } from '../../../administration/gestion-sites/store/site.store';
import { Cartographie } from '../../../cartographie/models/cartographie.model';
import { SidepanelService } from '../../../shared/components/sidepanel/sidepanel.service';
import { FiltresOffreRegionale } from '../../offre-regionale.component';
import { TrierParPipe } from './../../../shared/pipes/trier-par.pipe';
import { ReferentielStore } from '../../../administration/gestion-référentiels/store/referentiel.store';

@Component({
  selector: 'app-filtres',
  imports: [ReactiveFormsModule, HlmSelectImports, BrnSelectImports, HlmFieldImports, TrierParPipe, HlmButtonImports],
  templateUrl: './filtres.component.html',
})
export class FiltresComponent implements OnInit {
  readonly #fb = inject(FormBuilder);
  readonly #régionStore = inject(RégionStore);
  readonly #siteStore = inject(SiteStore);
  readonly #référentielStore = inject(ReferentielStore);
  readonly #destroyRef = inject(DestroyRef);
  readonly #sidepanelService = inject(SidepanelService);
  readonly #router = inject(Router);

  readonly filtres = input<FiltresOffreRegionale>();
  readonly cartographie = input.required<Cartographie>();

  readonly regions = computed(() => this.#régionStore.entities());
  readonly sites = linkedSignal(() => this.#siteStore.entities());
  readonly referentiels = computed(() => this.#référentielStore.entities());

  readonly form = this.#fb.group({
    site: [null],
    region: [null],
    metier: [null],
  });

  constructor() {
    effect(() => {
      this.#régionStore.charger(this.cartographie().id);
      this.#siteStore.charger(this.cartographie().id);
      this.#référentielStore.chargerParCartographie(this.cartographie().id);
    });

    effect(() => {
      const filtres = this.filtres();

      if (filtres) {
        this.site.setValue(filtres.site);
        this.region.setValue(filtres.region);
        this.metier.setValue(filtres.metier);

        if (filtres.region) {
          this.#filterSite(filtres.region);
        }
      }
    });
  }

  ngOnInit(): void {
    this.region.valueChanges
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((idRegion) => this.#filterSite(idRegion));

    this.#sidepanelService.submit.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => this.onSubmit());
  }

  onSubmit() {
    this.#router.navigate([], {
      queryParams: { site: this.site.value, region: this.region.value, metier: this.metier.value },
    });
  }

  #filterSite(idRegion: string) {
    if (!idRegion) {
      this.sites.set(this.#siteStore.entities());
      return;
    }
    const sites = this.#siteStore.entities().filter((x) => x.region === this.#régionStore.entityMap()[idRegion].nom);
    this.sites.set(sites);
    if (!sites.map((x) => x.id).includes(this.site.value)) {
      this.site.setValue(undefined);
    }
  }

  get region() {
    return this.form.get('region') as AbstractControl;
  }

  get site() {
    return this.form.get('site') as AbstractControl;
  }

  get metier() {
    return this.form.get('metier') as AbstractControl;
  }
}
